import type { Audience } from '../book/genres';
import type { Found } from '../add/add';
import { dayText } from '../book/model';

// Import a list, a typed or pasted list (My books Parts 4 and 5 step 1; agreed with Kristi Oct 31,
// mocked up in chat). The rules here have no screen, so the logic tests can check them.
//   Reading the paste (Kristi, Oct 31): one line is one book, in any capitals; numbers and bullets
//   dropped; blank lines don't matter. Nothing is guessed from capitals: the whole line is searched,
//   and a book fits when the line holds its title (and, when there are other words, its author's
//   surname helps). You join or split lines yourself when a photo put a title and author apart.
//   At most 100 books at a time (Kristi: the rest wait, "Match the next 82", nothing to edit).
//   Books already on your list are left out before any lookup, so pasting the whole list again
//   picks up where it stopped.

export const IMPORT_LIMIT = 100;

export interface Line {
  /** 1-based, in the paste's order. */
  seq: number;
  /** As you wrote it (lines you joined, with " / " between). */
  text: string;
  /** The line tidied (bullets and numbers dropped): what's searched and shown. */
  title: string;
  /** Only when the line says so: after " by ", or after a tab. A hint, never required. */
  author: string;
  /** You joined it with the line below it. */
  joined: boolean;
  /** The lines as pasted, so Split can put them back. */
  parts: string[];
}

const BULLET = /^\s*(?:[-*•·◦▪]|\(?\d{1,4}[.)\]:]|#\d+)\s+/;

/** "BEARTOWN" → "Beartown"; mixed case is left as typed. Only for showing a line; matching ignores case. */
export function tidyCase(s: string): string {
  const letters = s.replace(/[^A-Za-z]/g, '');
  if (letters.length < 3 || letters !== letters.toUpperCase()) return s;
  const small = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to']);
  return s
    .toLowerCase()
    .split(/(\s+)/)
    .map((w, i) => (/^\s+$/.test(w) || (i > 0 && small.has(w)) ? w : w.replace(/^(\W*)(\w)/, (_, p: string, c: string) => p + c.toUpperCase())))
    .join('');
}

/** One pasted line (Kristi, Oct 31: one line is one book; no guessing from capitals). */
export function lineOf(raw: string, seq: number, parts: string[] = [raw.trim()]): Line {
  const tab = raw.split('\t').map((x) => x.trim()).filter(Boolean);
  const clean = raw.replace(BULLET, '').replace(/\s+/g, ' ').trim();
  const by = clean.match(/^(.*\S)\s+by\s+(\S.*)$/i);
  const author = tab.length >= 2 ? tab.slice(1).join(' ') : by ? by[2]!.trim() : '';
  return { seq, text: raw.trim(), title: tab.length >= 2 ? tab.join(' ') : clean, author, joined: parts.length > 1, parts };
}

/** The paste as books, in order: every line that has words is one book. Blank lines don't matter. */
export function readPaste(text: string): Line[] {
  return text
    .split(/\r?\n/)
    .filter((raw) => raw.replace(BULLET, '').trim())
    .map((raw, i) => lineOf(raw, i + 1));
}

const renumber = (ls: Line[]) => ls.map((l, i) => ({ ...l, seq: i + 1 }));

/** Join with the line above: a photo can put a title and its author on two lines. */
export function joinUp(lines: Line[], seq: number): Line[] {
  const i = lines.findIndex((l) => l.seq === seq);
  if (i < 1) return lines;
  const parts = [...lines[i - 1]!.parts, ...lines[i]!.parts];
  const joined = lineOf(parts.map((p) => p.replace(BULLET, '')).join(' '), 0, parts);
  joined.text = parts.join(' / ');
  return renumber([...lines.slice(0, i - 1), joined, ...lines.slice(i + 1)]);
}

/** Split a joined line back into the lines you pasted. */
export function splitUp(lines: Line[], seq: number): Line[] {
  const i = lines.findIndex((l) => l.seq === seq);
  if (i < 0 || lines[i]!.parts.length < 2) return lines;
  return renumber([...lines.slice(0, i), ...lines[i]!.parts.map((p) => lineOf(p, 0)), ...lines.slice(i + 1)]);
}

/** What's searched for a line: its words, without " by ", dashes or the tab. */
export const queryOf = (l: Pick<Line, 'title'>) => l.title.replace(/\s+by\s+/gi, ' ').replace(/\s+[—–-]\s+/g, ' ').trim();

/** Titles and names compared without case, accents, punctuation, a subtitle or a leading The / A / An. */
export function normTitle(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/:.*$/, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/^\s*(the|a|an)\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();
}
export const surname = (name: string) =>
  normTitle(name.replace(/,?\s+(jr|sr|ii|iii|iv)\.?$/i, ''))
    .split(' ')
    .at(-1) ?? '';

const SMALL = new Set(['the', 'a', 'an', 'and', 'of', 'by', 'in', 'on', 'to', 'for', 'with', 'at', 'from', 'or']);
/** A line's words, any case. */
export function wordsOf(s: string): string[] {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}
const content = (ws: string[]) => [...new Set(ws.filter((w) => !SMALL.has(w)))];

/** How well a book fits a line, or null when it doesn't (Kristi, Oct 31): every word of its title
 * (before any subtitle) is in the line, and its title words plus an author's surname in the line
 * cover at least half the line's words. A publisher or other extra words are simply left over. */
export function fitOf(lineText: string, title: string, authors: string[], subtitle?: string | null): { score: number; author: boolean } | null {
  const lw = new Set(wordsOf(lineText));
  const want = content(wordsOf(lineText));
  const tw = content(wordsOf(normTitle(title)));
  if (!tw.length || !tw.every((w) => lw.has(w))) return null;
  const hits = new Set(tw);
  let author = false;
  for (const a of authors) {
    const s = surname(a);
    if (s && lw.has(s)) {
      author = true;
      hits.add(s);
    }
  }
  for (const w of content(wordsOf(`${title} ${subtitle ?? ''}`))) if (lw.has(w)) hits.add(w);
  if (hits.size * 2 < want.length) return null;
  return { score: hits.size, author };
}

/** A book on your list that this line names. */
export function onYourList<B extends { title: string; author: string; label: number }>(line: Pick<Line, 'title'>, books: B[]): B | null {
  return books.find((b) => fitOf(line.title, b.title, b.author ? [b.author] : [])) ?? null;
}

/** What the search found for a line: one book, a choice, or nothing that fits. */
export type Matched = { kind: 'matched'; found: Found; others: Found[] } | { kind: 'pick'; candidates: Found[] } | { kind: 'not_found' };

/** The books whose title is in the line win; one with an author's surname in the line beats one
 * without; the best fit is the book. Several different books that fit as well ask you to pick; one
 * book (however many records) matches. */
export function matchLine(line: Pick<Line, 'title'>, results: Found[]): Matched {
  const fits = results.map((r) => ({ r, f: fitOf(line.title, r.title, r.authors, r.subtitle) })).filter((x) => x.f) as { r: Found; f: { score: number; author: boolean } }[];
  if (!fits.length) return results.length ? { kind: 'pick', candidates: results.slice(0, 3) } : { kind: 'not_found' };
  const withAuthor = fits.filter((x) => x.f.author);
  const pool = withAuthor.length ? withAuthor : fits;
  const top = Math.max(...pool.map((x) => x.f.score));
  const best = pool.filter((x) => x.f.score === top).map((x) => x.r);
  const books = new Map<string, Found[]>();
  for (const r of best) {
    const k = `${normTitle(r.title)}|${surname(r.authors[0] ?? '')}`;
    books.set(k, [...(books.get(k) ?? []), r]);
  }
  if (books.size === 1) {
    // Every record of that book, for its facts; the one with the most editions, then Open Library's, leads.
    const k = [...books.keys()][0]!;
    const all = fits.map((x) => x.r).filter((r) => `${normTitle(r.title)}|${surname(r.authors[0] ?? '')}` === k);
    const lead = [...all].sort((a, b) => (b.editions ?? 0) - (a.editions ?? 0) || (a.source === 'Open Library' ? -1 : 1))[0]!;
    // Fiction if any record says so (Add a book's rule); the summary from whichever has one.
    const merged: Found = { ...lead, fiction: all.some((x) => x.fiction) || lead.fiction, summary: lead.summary ?? all.find((x) => x.summary)?.summary ?? null };
    if (!lead.summary && merged.summary) merged.summarySource = all.find((x) => x.summary)?.summarySource ?? null;
    return { kind: 'matched', found: merged, others: results.filter((r) => !all.includes(r)).slice(0, 3) };
  }
  return { kind: 'pick', candidates: [...books.values()].map((g) => g[0]!).slice(0, 3) };
}

/** The import's shelf: no shelf yet by default (Kristi: you review it in Recently added › No shelf yet). */
export type ImportShelf = 'none' | 'future_reads' | 'up_next' | 'reference';
export const IMPORT_SHELVES: { key: ImportShelf; label: string }[] = [
  { key: 'none', label: 'No shelf yet' },
  { key: 'future_reads', label: 'Future reads' },
  { key: 'up_next', label: 'End of Up Next' },
  { key: 'reference', label: 'Reference' },
];
export const shelfWords = (s: ImportShelf) => (s === 'none' ? 'with no shelf yet' : s === 'up_next' ? 'to the end of Up Next' : s === 'future_reads' ? 'to Future reads' : 'to Reference');

/** A row's copy (Kristi: chosen at import, laptop-first). */
// A paper book you no longer have can come in as lost or donated (check-up, Kristi Nov 2): its copy
// is added, then marked with the copy's own lost / donated state right after the import (no
// database change, option a). Undo import keeps those books, since they changed after it.
export type ImportCopy = 'none' | 'kindle' | 'paper' | 'audible' | 'paper_lost' | 'paper_donated';
/** Every choice a row's Copy ▾ offers. */
export const IMPORT_COPIES: { key: ImportCopy; label: string }[] = [
  { key: 'none', label: 'Not owned yet' },
  { key: 'kindle', label: 'Kindle' },
  { key: 'paper', label: 'Paper' },
  { key: 'audible', label: 'Audible' },
  { key: 'paper_lost', label: 'Paper · lost' },
  { key: 'paper_donated', label: 'Paper · donated' },
];
/** What are these?, for every row at once: the copies you have (lost and donated are per row). */
export const ALL_ROW_COPIES = IMPORT_COPIES.filter((c) => c.key !== 'paper_lost' && c.key !== 'paper_donated');
/** The state a row's copy is marked with after the import, if any. */
export const copyStateOf = (c: ImportCopy): 'lost' | 'donated' | null => (c === 'paper_lost' ? 'lost' : c === 'paper_donated' ? 'donated' : null);
const formatOf = (c: ImportCopy) => (c === 'paper_lost' || c === 'paper_donated' ? 'paper' : c);

export type Literary = 'fiction' | 'nonfiction' | 'poetry' | 'drama';

/** A row being checked. */
export interface Row extends Line {
  state: 'matching' | 'matched' | 'pick' | 'not_found' | 'on_list' | 'skipped' | 'error';
  found: Found | null;
  candidates: Found[];
  /** Everything the search found, for "Show more" when none of the first few is it (Kristi, Nov 1). */
  more?: Found[];
  /** B-number when already on your list. */
  onList: number | null;
  /** The book on your list (0049: Add a copy to it). */
  onListBook?: string | null;
  /** Already on your list (Kristi, Nov 1, as board 10): add the row's copy, or leave yours as it is; null until you say. */
  confirm?: 'add' | 'keep' | null;
  /** The formats you have of that book ("Kindle"), for its line. */
  have?: string[];
  literary: Literary | null;
  copy: ImportCopy;
  error?: string;
}

/** A row ready to import: a book picked and its literary genre known (from the match, else you). */
export const ready = (r: Row) => r.state === 'matched' && Boolean(r.found) && Boolean(r.literary);
/** Waiting for you: a book to pick, a literary genre to choose, or a book you have to confirm. */
export const waiting = (r: Row) => r.state === 'pick' || (r.state === 'matched' && !r.literary) || (r.state === 'on_list' && !r.confirm);
/** A matched row still missing its literary genre (Kristi, Nov 1: say so, and highlight it). */
export const needsLiterary = (r: Row) => r.state === 'matched' && !r.literary;
/** A row that adds a copy to a book you have (0049). */
export const addsCopy = (r: Row) => r.state === 'on_list' && r.confirm === 'add' && r.copy !== 'none' && Boolean(r.onListBook);

/** The copy import_list gets for a row's choice. */
export function copyOf(c: ImportCopy): Record<string, unknown> | undefined {
  if (c === 'none') return undefined;
  const f = formatOf(c);
  return { how: 'bought', format: f, listen: f === 'audible', ...(f === 'kindle' ? { store: 'Amazon' } : f === 'audible' ? { store: 'Audible' } : {}) };
}
export const copyLabel = (c: ImportCopy) => IMPORT_COPIES.find((x) => x.key === c)!.label;
/** In a sentence: "a Kindle copy", "a paper copy, marked lost". */
export const copyPhrase = (c: ImportCopy) => {
  const st = copyStateOf(c);
  return st ? `a paper copy, marked ${st}` : `a ${copyLabel(c)} copy`;
};

/** What book_add gets for one row (0048: details.import_batch is added by import_list). */
export function bookFor(r: Row, shelf: ImportShelf, audience: Audience = 'adult'): Record<string, unknown> {
  const f = r.found!;
  // Audience: Adult unless you pick another for the batch (Kristi, Nov 2).
  const d: Record<string, unknown> = { shelf, title: f.title, authors: f.authors, literary_genre: r.literary, audience };
  if (f.subtitle) d.subtitle = f.subtitle;
  if (f.year) d.first_year = f.year;
  if (f.summary) {
    d.summary = f.summary;
    d.summary_source = f.summarySource;
  }
  if (f.openLibraryId) d.open_library_id = f.openLibraryId;
  if (f.googleBooksId) d.google_books_id = f.googleBooksId;
  const fmt = formatOf(r.copy);
  const kind = fmt === 'kindle' ? 'ebook' : fmt === 'audible' ? 'audiobook' : fmt === 'paper' ? 'print' : (f.kind ?? 'print');
  const e: Record<string, unknown> = { kind };
  if (f.coverUrl?.startsWith('https://')) e.cover_url = f.coverUrl;
  if (f.pages && kind !== 'audiobook') e.pages = f.pages;
  if (f.isbn13 && kind === 'print') e.isbn13 = f.isbn13;
  d.edition = e;
  const copy = copyOf(r.copy);
  if (copy) d.copy = copy;
  return d;
}

/** Every line for import_list, in order: added, left out (on your list), skipped or not found. */
export function itemsFor(
  rows: Row[],
  shelf: ImportShelf,
  audience: Audience = 'adult',
): { seq: number; line: string; outcome: string; book?: Record<string, unknown>; book_id?: string; copy?: Record<string, unknown>; note?: string }[] {
  return rows.map((r) => {
    const line = r.text.slice(0, 1000);
    if (ready(r)) return { seq: r.seq, line, outcome: 'add', book: bookFor(r, shelf, audience) };
    const b = `B${String(r.onList).padStart(4, '0')}`;
    if (addsCopy(r)) return { seq: r.seq, line, outcome: 'add_copy', book_id: r.onListBook!, copy: copyOf(r.copy)!, note: `${copyPhrase(r.copy).replace(/^a/, 'A')} added to ${b}` };
    if (r.state === 'on_list')
      return { seq: r.seq, line, outcome: 'left_out', ...(r.onListBook ? { book_id: r.onListBook } : {}), note: r.confirm ? `Already on your list as ${b}; left as it was` : `Already on your list as ${b}; not confirmed` };
    if (r.state === 'not_found') return { seq: r.seq, line, outcome: 'not_found' };
    return { seq: r.seq, line, outcome: 'skipped', note: waiting(r) ? 'Waiting for a choice' : r.state === 'error' ? (r.error ?? '') : '' };
  });
}

/** The first batch of lines and what waits: at most 100 books not already on your list. */
export function firstBatch<B extends { title: string; author: string; label: number }>(lines: Line[], books: B[]): { now: Line[]; rest: Line[] } {
  const now: Line[] = [];
  let count = 0;
  let i = 0;
  for (; i < lines.length; i++) {
    const l = lines[i]!;
    if (!onYourList(l, books)) {
      if (count === IMPORT_LIMIT) break;
      count++;
    }
    now.push(l);
  }
  return { now, rest: lines.slice(i) };
}

/** "Typed list · Oct 31", through the one date formatter (it's today, so no year). */
export function defaultName(today: string): string {
  return `Typed list · ${dayText(today, today)}`;
}

/**
 * The copies to mark lost or donated once the import has run: the paper copy of each new book
 * (from the import's result) or the one just added to a book you had, for rows that asked.
 */
export function statesToMark(rows: Row[], added: { seq: number; book: string }[]): { book: string; state: 'lost' | 'donated'; title: string }[] {
  const bySeq = new Map(added.map((a) => [a.seq, a.book]));
  const out: { book: string; state: 'lost' | 'donated'; title: string }[] = [];
  for (const r of rows) {
    const state = copyStateOf(r.copy);
    if (!state) continue;
    const book = ready(r) ? bySeq.get(r.seq) : addsCopy(r) ? r.onListBook : null;
    if (book) out.push({ book, state, title: r.found?.title ?? r.text });
  }
  return out;
}
